/**
 * Sections table + section editor in a modal (plain text fields).
 */

import { useFieldArray, useWatch } from 'react-hook-form'
import { Plus, Trash2 } from 'lucide-react'
import type {
  ChecklistTemplateMasterDetailProps,
  ChecklistSectionDetailProps,
} from '../types'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
  deleteEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import parityStyles from '@/features/invitee/styles/invitesParity.module.css'

export function ChecklistTemplateMasterDetail({
  control,
  register,
  errors,
  sectionFields,
  onAddSection,
  onRemoveSection,
  sectionModalIndex,
  setSectionModalIndex,
  onDismissSectionModal,
  parentFormId,
  submitLabel,
  submitLoading,
}: ChecklistTemplateMasterDetailProps) {
  const watchedSections = useWatch({ control, name: 'checkbox_data' })

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-slate-900">Checklist</h2>
        <p className="text-sm font-medium text-slate-700">Checklist Checkbox Section</p>
      </div>

      <div
        className={parityStyles.tableCard}
        role="region"
        aria-label="Sections list"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50">
          <span className="text-sm font-medium text-slate-800">Sections</span>
          <button
            type="button"
            onClick={onAddSection}
            className={cn(primaryEntityActionButtonClass, 'h-9 gap-1.5 px-3.5 whitespace-nowrap')}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Add New Section
          </button>
        </div>

        {sectionFields.length === 0 ? (
          <p className="text-sm text-slate-500 p-4">
            No sections yet. Click &quot;Add New Section&quot; to create one.
          </p>
        ) : (
          <div className={cn(parityStyles.tableResponsiveScroll, 'touch-scroll')}>
            <table className={cn(parityStyles.listingTable, parityStyles.propertyListingTable, 'table-fixed w-full min-w-[50rem] !max-w-none [&_thead_th]:!whitespace-nowrap [&_tbody_td:not([colspan])]:!whitespace-normal [&_tbody_td:not([colspan])]:break-words [&_thead_th]:!px-2.5 [&_thead_th]:!py-2 [&_thead_th]:!align-middle [&_tbody_td]:!px-2.5 [&_tbody_td]:!py-2 [&_tbody_td]:!text-[13px] [&_tbody_td]:!leading-snug')} role="grid">
              <thead>
                <tr>
                  <th scope="col" className="!text-left">Key</th>
                  <th scope="col" className="!text-left">Label</th>
                  <th scope="col" className="!text-center">Items</th>
                  <th scope="col" className="!text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {sectionFields.map((row, i) => {
                  const section = watchedSections?.[i]
                  const preview = section?.label?.trim() || '—'
                  const nChild = section?.child?.length ?? 0
                  const modalOpen = sectionModalIndex === i
                  return (
                    <tr
                      key={row.id}
                      role="row"
                      aria-selected={modalOpen}
                      data-testid={`checklist-master-row-${i}`}
                      className={cn(
                        'cursor-pointer transition-colors hover:bg-slate-50/50',
                        modalOpen && 'bg-button-soft'
                      )}
                      onClick={() => setSectionModalIndex(i)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setSectionModalIndex(i)
                        }
                      }}
                      tabIndex={0}
                    >
                      <td className="align-top font-mono text-xs text-slate-900">
                        {section?.key || '—'}
                      </td>
                      <td className="align-top text-slate-600 max-w-[200px] truncate">
                        {preview}
                      </td>
                      <td className="!text-center text-slate-700">{nChild}</td>
                      <td className="!text-center">
                        <button
                          type="button"
                          className={cn(
                            deleteEntityActionButtonClass,
                            'h-8 min-w-0 gap-1.5 px-3 text-xs',
                          )}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            onRemoveSection(i)
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          Remove Section
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <OnScreenModal
        isOpen={sectionModalIndex !== null}
        title={sectionModalIndex !== null ? `Section ${sectionModalIndex + 1}` : 'Section'}
        onClose={onDismissSectionModal}
        maxWidth="max-w-2xl"
        overlayDataTestId="checklist-section-modal-overlay"
        panelDataTestId="checklist-section-modal-panel"
      >
        {sectionModalIndex !== null && (
          <>
            <ChecklistSectionDetail
              fieldName={`checkbox_data.${sectionModalIndex}`}
              sectionIndex={sectionModalIndex}
              control={control}
              register={register}
              errors={errors}
            />
            <div className="flex flex-wrap justify-center gap-2 pt-4 mt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={onDismissSectionModal}
                className={secondaryEntityActionButtonClass}
              >
                Cancel
              </button>
              <button
                type="submit"
                form={parentFormId}
                disabled={submitLoading}
                className={primaryEntityActionButtonClass}
              >
                {submitLabel}
              </button>
            </div>
          </>
        )}
      </OnScreenModal>
    </div>
  )
}

function ChecklistSectionDetail({
  fieldName,
  sectionIndex,
  control,
  register,
  errors,
}: ChecklistSectionDetailProps) {
  const sectionErrors = errors.checkbox_data?.[sectionIndex]

  const {
    fields: childFields,
    append: appendChild,
    remove: removeChild,
  } = useFieldArray({ control, name: `${fieldName}.child` })

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Key</label>
        <input
          type="text"
          placeholder="Key"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-button/30"
          {...register(`${fieldName}.key`)}
        />
        {sectionErrors?.key && (
          <p className="text-xs text-red-600 mt-1">{sectionErrors.key.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Label</label>
        <input
          type="text"
          placeholder="Label"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-button/30"
          {...register(`${fieldName}.label`)}
        />
      </div>

      <div className="border-t border-slate-200 pt-3">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <span className="text-sm font-medium text-slate-800">Sub-sections</span>
          {childFields.length === 0 && (
            <button
              type="button"
              onClick={() => appendChild({ key: '', label: '', description: '', hasInput: false, isLock: 0 })}
              className={cn(primaryEntityActionButtonClass, 'h-9 gap-1.5 px-3.5 whitespace-nowrap')}
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              Add Sub Section
            </button>
          )}
        </div>

        {childFields.length === 0 ? (
          <p className="text-xs text-slate-500">No sub-sections yet.</p>
        ) : (
          <div className="overflow-x-auto rounded border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[#dfeaf5] text-slate-900 text-xs border-b border-[#c8d7e8]">
                <tr>
                  <th className="px-2 py-2 text-left font-bold">Key</th>
                  <th className="px-2 py-2 text-left font-bold">Label</th>
                  <th className="px-2 py-2 text-left font-bold w-40">Action</th>
                </tr>
              </thead>
              <tbody>
                {childFields.map((cf, childIndex) => {
                  const childErrors = sectionErrors?.child?.[childIndex]
                  const isLast = childIndex === childFields.length - 1
                  return (
                    <tr key={cf.id} className="border-b border-[#dfe7f0]">
                      <td className="px-2 py-2 align-top">
                        <input
                          type="text"
                          placeholder="Key"
                          className="w-full min-w-[100px] rounded border border-slate-300 px-2 py-1 text-sm"
                          {...register(`${fieldName}.child.${childIndex}.key`)}
                        />
                        {childErrors?.key && (
                          <p className="text-xs text-red-600 mt-0.5">{childErrors.key.message}</p>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top">
                        <input
                          type="text"
                          placeholder="Label"
                          className="w-full min-w-[140px] rounded border border-slate-300 px-2 py-1 text-sm"
                          {...register(`${fieldName}.child.${childIndex}.label`)}
                        />
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap align-middle">
                        {isLast && (
                          <button
                            type="button"
                            onClick={() => appendChild({ key: '', label: '', description: '', hasInput: false, isLock: 0 })}
                            className={cn(
                              secondaryEntityActionButtonClass,
                              'mr-1 h-8 min-w-0 gap-1 px-2.5 text-xs',
                            )}
                          >
                            <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            Add
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeChild(childIndex)}
                          className={cn(
                            deleteEntityActionButtonClass,
                            'h-8 min-w-0 gap-1 px-2.5 text-xs',
                          )}
                        >
                          <Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          Remove
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
